import { useState, useCallback, useRef } from 'react';
import type { Point } from '@/types';
import { distance } from '@/utils/geometry';

/** Minimum distance from center to allow drawing */
const MIN_RADIUS_FROM_CENTER = 100;

/** Distance threshold for closing the loop */
const LOOP_CLOSURE_THRESHOLD = 30;

/** Minimum points required for a valid boundary */
const MIN_POINTS_FOR_BOUNDARY = 10;

export interface Boundary {
  /** The points forming the closed boundary */
  points: Point[];
  /** Precomputed angle to radius lookup (0-359 degrees) */
  angleToRadius: Map<number, number>;
}

export interface UseBoundaryResult {
  /** The completed boundary (null if none) */
  boundary: Boundary | null;
  /** Points currently being drawn */
  drawingPoints: Point[];
  /** Whether user is currently drawing */
  isDrawing: boolean;
  /** Whether a rejection animation is playing */
  isRejected: boolean;
  /** Start drawing at the given point */
  startDrawing: (point: Point, center: Point) => void;
  /** Continue drawing to the given point */
  continueDrawing: (point: Point, center: Point) => void;
  /** End drawing, checking for loop closure */
  endDrawing: (center: Point) => void;
  /** Clear the boundary */
  clearBoundary: () => void;
  /** Recalculate boundary distances from a new center point */
  recalculateBoundary: (center: Point) => void;
  /** Set a pre-defined boundary (for demo mode) */
  setDemoBoundary: (points: Point[], center: Point) => void;
  /** Set drawing points directly (for demo animation) */
  setDemoDrawingPoints: (points: Point[]) => void;
}

/** Duration of rejection animation in ms */
const REJECTION_ANIMATION_DURATION = 400;

/**
 * Hook to manage boundary drawing state and interaction.
 */
export const useBoundary = (): UseBoundaryResult => {
  const [boundary, setBoundary] = useState<Boundary | null>(null);
  const [drawingPoints, setDrawingPoints] = useState<Point[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isRejected, setIsRejected] = useState(false);
  const startPointRef = useRef<Point | null>(null);
  const rejectionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startDrawing = useCallback((point: Point, center: Point) => {
    // Only start if outside minimum radius
    if (distance(point, center) < MIN_RADIUS_FROM_CENTER) {
      return;
    }

    startPointRef.current = point;
    setDrawingPoints([point]);
    setIsDrawing(true);
  }, []);

  const continueDrawing = useCallback((point: Point, center: Point) => {
    if (!isDrawing) return;

    // Enforce minimum radius from center
    const distFromCenter = distance(point, center);
    if (distFromCenter < MIN_RADIUS_FROM_CENTER) {
      // Project point to minimum radius
      const angle = Math.atan2(point.y - center.y, point.x - center.x);
      point = {
        x: center.x + Math.cos(angle) * MIN_RADIUS_FROM_CENTER,
        y: center.y + Math.sin(angle) * MIN_RADIUS_FROM_CENTER,
      };
    }

    setDrawingPoints((prev) => [...prev, point]);
  }, [isDrawing]);

  const triggerRejection = useCallback(() => {
    // Clear any existing timeout
    if (rejectionTimeoutRef.current) {
      clearTimeout(rejectionTimeoutRef.current);
    }
    
    setIsRejected(true);
    rejectionTimeoutRef.current = setTimeout(() => {
      setIsRejected(false);
    }, REJECTION_ANIMATION_DURATION);
  }, []);

  const endDrawing = useCallback((center: Point) => {
    if (!isDrawing || !startPointRef.current) {
      setIsDrawing(false);
      setDrawingPoints([]);
      return;
    }

    const startPoint = startPointRef.current;
    const lastPoint = drawingPoints[drawingPoints.length - 1];

    // Check if we have enough points and the loop is closed
    const isLoopClosed =
      drawingPoints.length >= MIN_POINTS_FOR_BOUNDARY &&
      lastPoint &&
      distance(lastPoint, startPoint) <= LOOP_CLOSURE_THRESHOLD;

    if (isLoopClosed) {
      // Close the loop and smooth it
      const closedPoints = [...drawingPoints, startPoint];
      const smoothedPoints = smoothPath(closedPoints);
      
      // Verify center is inside the boundary
      if (!isPointInsidePolygon(center, smoothedPoints)) {
        // Center is outside - reject with visual feedback
        triggerRejection();
        setIsDrawing(false);
        setDrawingPoints([]);
        startPointRef.current = null;
        return;
      }
      
      // Create the boundary
      const angleToRadius = precomputeAngleToRadius(smoothedPoints, center);
      
      setBoundary({
        points: smoothedPoints,
        angleToRadius,
      });
    } else if (drawingPoints.length >= MIN_POINTS_FOR_BOUNDARY) {
      // Had enough points but loop wasn't closed - show rejection
      triggerRejection();
    }

    // Reset drawing state
    setIsDrawing(false);
    setDrawingPoints([]);
    startPointRef.current = null;
  }, [isDrawing, drawingPoints, triggerRejection]);

  const clearBoundary = useCallback(() => {
    setBoundary(null);
    setDrawingPoints([]);
    setIsDrawing(false);
    startPointRef.current = null;
  }, []);

  const recalculateBoundary = useCallback((center: Point) => {
    if (!boundary) return;
    
    // Recalculate angle→radius from new center position
    const angleToRadius = precomputeAngleToRadius(boundary.points, center);
    
    setBoundary({
      ...boundary,
      angleToRadius,
    });
  }, [boundary]);

  const setDemoBoundary = useCallback((points: Point[], center: Point) => {
    if (points.length < 3) return; // Need at least 3 points for a shape
    
    const firstPoint = points[0];
    const lastPoint = points[points.length - 1];
    
    // Close the loop if not already closed
    let closedPoints = points;
    if (firstPoint && lastPoint && 
        (firstPoint.x !== lastPoint.x || firstPoint.y !== lastPoint.y)) {
      closedPoints = [...points, firstPoint];
    }
    
    // Apply smoothing for a nice appearance
    const smoothedPoints = smoothPath(closedPoints);
    
    // Compute angle to radius lookup
    const angleToRadius = precomputeAngleToRadius(smoothedPoints, center);
    
    // Clear drawing state when setting final boundary
    setDrawingPoints([]);
    setIsDrawing(false);
    
    setBoundary({
      points: smoothedPoints,
      angleToRadius,
    });
  }, []);

  const setDemoDrawingPoints = useCallback((points: Point[]) => {
    setDrawingPoints(points);
    setIsDrawing(points.length > 0);
  }, []);

  return {
    boundary,
    drawingPoints,
    isDrawing,
    isRejected,
    startDrawing,
    continueDrawing,
    endDrawing,
    clearBoundary,
    recalculateBoundary,
    setDemoBoundary,
    setDemoDrawingPoints,
  };
};

/**
 * Smooth a closed path using Chaikin's corner cutting algorithm.
 * This replaces sharp corners with smoother curves.
 */
function smoothPath(points: Point[], iterations: number = 2): Point[] {
  if (points.length < 3) return points;

  let result = points;

  for (let iter = 0; iter < iterations; iter++) {
    const smoothed: Point[] = [];
    const n = result.length;

    for (let i = 0; i < n; i++) {
      const p0 = result[i];
      const p1 = result[(i + 1) % n];

      if (!p0 || !p1) continue;

      // Chaikin's algorithm: create two new points at 1/4 and 3/4 along each segment
      smoothed.push({
        x: 0.75 * p0.x + 0.25 * p1.x,
        y: 0.75 * p0.y + 0.25 * p1.y,
      });
      smoothed.push({
        x: 0.25 * p0.x + 0.75 * p1.x,
        y: 0.25 * p0.y + 0.75 * p1.y,
      });
    }

    result = smoothed;
  }

  // Ensure the path is closed
  if (result.length > 0 && result[0]) {
    result.push({ ...result[0] });
  }

  return result;
}

/**
 * Check if a point is inside a polygon using ray casting algorithm.
 */
function isPointInsidePolygon(point: Point, polygon: Point[]): boolean {
  let inside = false;
  
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const pi = polygon[i];
    const pj = polygon[j];
    
    if (!pi || !pj) continue;
    
    const xi = pi.x, yi = pi.y;
    const xj = pj.x, yj = pj.y;
    
    if (((yi > point.y) !== (yj > point.y)) &&
        (point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  
  return inside;
}

/**
 * Precompute the distance from center to boundary at each degree (0-359).
 */
function precomputeAngleToRadius(points: Point[], center: Point): Map<number, number> {
  const angleToRadius = new Map<number, number>();

  for (let angle = 0; angle < 360; angle++) {
    const radius = raycastToBoundary(center, angle, points);
    angleToRadius.set(angle, radius);
  }

  return angleToRadius;
}

/**
 * Cast a ray from center at the given angle and find intersection with boundary.
 */
function raycastToBoundary(center: Point, angleDegrees: number, points: Point[]): number {
  // Convert clock angle to standard math angle (radians)
  // Clock: 0° = up, clockwise
  const angleRad = ((angleDegrees - 90) * Math.PI) / 180;
  const rayDir = { x: Math.cos(angleRad), y: Math.sin(angleRad) };

  let minDistance = Infinity;

  // Check intersection with each line segment
  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i];
    const p2 = points[i + 1];
    
    if (!p1 || !p2) continue;

    const intersection = rayLineIntersection(center, rayDir, p1, p2);
    if (intersection !== null && intersection < minDistance) {
      minDistance = intersection;
    }
  }

  return minDistance;
}

/**
 * Find the distance along a ray to its intersection with a line segment.
 * Returns null if no intersection.
 */
function rayLineIntersection(
  rayOrigin: Point,
  rayDir: { x: number; y: number },
  lineStart: Point,
  lineEnd: Point
): number | null {
  const v1 = { x: rayOrigin.x - lineStart.x, y: rayOrigin.y - lineStart.y };
  const v2 = { x: lineEnd.x - lineStart.x, y: lineEnd.y - lineStart.y };
  const v3 = { x: -rayDir.y, y: rayDir.x };

  const dot = v2.x * v3.x + v2.y * v3.y;
  if (Math.abs(dot) < 0.000001) {
    return null; // Parallel
  }

  const t1 = (v2.x * v1.y - v2.y * v1.x) / dot;
  const t2 = (v1.x * v3.x + v1.y * v3.y) / dot;

  if (t1 >= 0 && t2 >= 0 && t2 <= 1) {
    return t1;
  }

  return null;
}
